import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

export default function StartRunScriptDialog({
  open,
  onOpenChange,
  leadId,
  prospect1Name,
  appointmentId,
}) {
  const { toast } = useToast();
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [productTypes, setProductTypes] = useState([]);
  const [scriptTypes, setScriptTypes] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [appointmentTeamId, setAppointmentTeamId] = useState("");


  const [productTypeId, setProductTypeId] = useState("");
  const [scriptTypeId, setScriptTypeId] = useState("");
  useEffect(() => {
    if (!open) return;
    if (!appointmentId) {
      setAppointmentTeamId("");
      return;
    }

    const loadAppointment = async () => {
      try {
        const res = await api.get(`/appointments/${appointmentId}`);
        setAppointmentTeamId(res.data?.team_id || "");
      } catch {
        setAppointmentTeamId("");
      }
    };

    loadAppointment();
  }, [open, appointmentId]);

  const [scriptId, setScriptId] = useState("");

  useEffect(() => {
    if (!open) return;

    const load = async () => {
      try {
        const pts = await api.get("/product-types", {
          params: appointmentTeamId ? { team_id: appointmentTeamId } : {},
        });
        setProductTypes(pts.data || []);
      } catch {
        toast({ title: "Could not load playbooks", variant: "destructive" });
      }
    };

    load();
    setProductTypeId("");
    setScriptTypeId("");
    setScriptId("");
    setScriptTypes([]);
    setScripts([]);
  }, [open, toast, appointmentTeamId]);

  useEffect(() => {
    if (!productTypeId) return;

    const load = async () => {
      try {
        const sts = await api.get(`/product-types/${productTypeId}/script-types`);
        setScriptTypes(sts.data || []);
      } catch {
        setScriptTypes([]);
      }
    };

    load();
    setScriptTypeId("");
    setScriptId("");
    setScripts([]);
  }, [productTypeId]);

  useEffect(() => {
    if (!productTypeId || !scriptTypeId) return;

    const load = async () => {
      try {
        const ss = await api.get(`/product-types/${productTypeId}/scripts`, {
          params: { script_type_id: scriptTypeId },
        });
        setScripts(ss.data || []);
      } catch {
        setScripts([]);
      }
    };

    load();
    setScriptId("");
  }, [productTypeId, scriptTypeId]);

  const onStart = async () => {
    if (!scriptId) return;
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (leadId) params.set("lead_id", leadId);
      if (prospect1Name) params.set("p1", prospect1Name);
      if (appointmentId) params.set("appointment_id", appointmentId);

      const qs = params.toString();
      navigate(`/app/scripts/${scriptId}/play${qs ? `?${qs}` : ""}`);
      onOpenChange?.(false);
    } catch (e) {
      toast({
        title: "Could not start run",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-xl">
        <DialogHeader>
          <DialogTitle>Start Run</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-200/80">Playbook</Label>
            <Select value={productTypeId} onValueChange={setProductTypeId}>
              <SelectTrigger className="bg-white/5 ring-1 ring-white/10">
                <SelectValue placeholder="Select playbook" />
              </SelectTrigger>
              <SelectContent className="glass border-white/10">
                {productTypes.map((pt) => (
                  <SelectItem key={pt.id} value={pt.id}>
                    {pt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-200/80">Script Type</Label>
            <Select
              value={scriptTypeId}
              onValueChange={setScriptTypeId}
              disabled={!productTypeId}
            >
              <SelectTrigger className="bg-white/5 ring-1 ring-white/10">
                <SelectValue
                  placeholder={
                    productTypeId ? "Select script type" : "Select playbook first"
                  }
                />
              </SelectTrigger>
              <SelectContent className="glass border-white/10">
                {scriptTypes.map((st) => (
                  <SelectItem key={st.id} value={st.id}>
                    {st.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-200/80">Script</Label>
            <Select value={scriptId} onValueChange={setScriptId} disabled={!scriptTypeId}>
              <SelectTrigger className="bg-white/5 ring-1 ring-white/10">
                <SelectValue
                  placeholder={scriptTypeId ? "Select script" : "Select script type first"}
                />
              </SelectTrigger>
              <SelectContent className="glass border-white/10">
                {scripts.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange?.(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={onStart}
            disabled={busy || !scriptId}
          >
            {busy ? "Starting…" : "Start"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
